import { useEffect, useMemo } from 'react';
import type { CSSProperties } from 'react';
import { Download } from 'lucide-react';
import { Spinner } from '@/components/ui/spinner';
import { Button } from '@/components/ui/button';
import { OnboardingTable } from '@/components/ui/onboarding-table';
import {
  useIngestionStatus,
  useGenerationStatus,
  useAnalyzeGapMutation,
  type GapAnalysisResponse,
  type CoveredMethod,
  type NotCoveredMethod,
} from '@/components/smartai/api/testIntelligence';
import downloadCsv from '@/utils/downloadCsv';

// ── Types ─────────────────────────────────────────────────────────────────────

interface TestMappingViewProps {
  ingestionId: string;
  generatedId: string | null;
  isMappingLoading: boolean;
  thresholdValue: number;
  gapAnalysis: GapAnalysisResponse | null;
  onGapAnalysisReceived: (data: GapAnalysisResponse) => void;
  componentName: string;
}

type MergedRow =
  | (CoveredMethod & { _isCovered: true })
  | (NotCoveredMethod & { _isCovered: false });

// ── Sub-components ────────────────────────────────────────────────────────────

const StatCard = ({
  title,
  value,
}: {
  title: string;
  value: string | number;
}) => (
  <div className="rounded-lg border border-[var(--border)] bg-[var(--gray-50,#f9f9f9)] p-4">
    <div className="mb-2 text-xs font-semibold uppercase tracking-[0.04em] text-[var(--text-secondary)]">
      {title}
    </div>
    <div className="text-xl font-bold text-[var(--text-primary)]">{value}</div>
  </div>
);

const loaderWrapClass =
  'flex flex-col items-center gap-4 py-8';
const loaderLabelClass = 'text-sm text-[var(--text-secondary)]';
const changeBadgeClass =
  'inline-block rounded px-2 py-0.5 text-xs font-medium capitalize bg-[var(--gray-100,#eee)] text-[var(--text-secondary)]';
const statusBadgeClass =
  'inline-block rounded px-2 py-0.5 text-xs font-semibold bg-[color-mix(in_srgb,var(--badge-color)_15%,transparent)] text-[var(--badge-color)]';
const testListClass = 'm-0 flex list-none flex-col gap-1 p-0 text-[0.8rem] text-[var(--text-primary)]';
const csBadgeClass =
  'ml-1.5 inline-block rounded-[3px] bg-[var(--gray-100,#eee)] px-1.5 py-px text-[0.7rem] font-semibold text-[var(--text-secondary)]';

// ── Main component ────────────────────────────────────────────────────────────

const TestMappingView = ({
  ingestionId,
  generatedId,
  isMappingLoading,
  thresholdValue,
  gapAnalysis,
  onGapAnalysisReceived,
  componentName,
}: TestMappingViewProps) => {
  const { data: ingestionStatus } = useIngestionStatus(
    ingestionId,
    !isMappingLoading && !!ingestionId,
  );
  const ingestionDone = ingestionStatus?.status === 'completed';

  const { data: generationStatus } = useGenerationStatus(
    generatedId,
    ingestionDone && !!generatedId,
  );
  const generationDone = generationStatus?.status === 'completed';

  const { mutate: analyzeGap, isPending: isAnalyzing } =
    useAnalyzeGapMutation();

  useEffect(() => {
    if (
      ingestionDone &&
      generationDone &&
      gapAnalysis === null &&
      generatedId
    ) {
      analyzeGap(
        {
          generationId: generatedId,
          similarityThreshold: thresholdValue / 100,
        },
        { onSuccess: onGapAnalysisReceived },
      );
    }
  }, [ingestionDone, generationDone, gapAnalysis, generatedId]);

  const isPolling = !isMappingLoading && gapAnalysis === null && !isAnalyzing;

  const mergedRows: MergedRow[] = useMemo(() => {
    if (!gapAnalysis) return [];
    return [
      ...gapAnalysis.covered.map(m => ({
        ...m,
        _isCovered: true as const,
      })),
      ...gapAnalysis.notCovered.map(m => ({
        ...m,
        _isCovered: false as const,
      })),
    ];
  }, [gapAnalysis]);

  const columns = useMemo(
    () => [
      {
        key: 'method',
        header: 'Method',
        render: (row: MergedRow) => (
          <span className="break-all font-mono text-[0.8rem]">{row.method}</span>
        ),
      },
      {
        key: 'filePath',
        header: 'File Path',
        render: (row: MergedRow) => (
          <span className="break-all text-[var(--inv-text-secondary)]">
            {row.filePath}
          </span>
        ),
      },
      {
        key: 'changeType',
        header: 'Change',
        render: (row: MergedRow) => (
          <span className={changeBadgeClass}>{row.changeType}</span>
        ),
      },
      {
        key: 'status',
        header: 'Status',
        render: (row: MergedRow) => (
          <span
            className={statusBadgeClass}
            style={
              {
                '--badge-color': row._isCovered
                  ? 'var(--color-success, #2e7d32)'
                  : 'var(--color-error, #c62828)',
              } as CSSProperties
            }>
            {row._isCovered ? 'MAPPED' : 'NOT MAPPED'}
          </span>
        ),
      },
      {
        key: 'tests',
        header: 'Tests',
        render: (row: MergedRow) => (
          <span>{row._isCovered ? row.matchingAdoTestCases.length : 0}</span>
        ),
      },
      {
        key: 'ado',
        header: 'ADO Test Cases',
        render: (row: MergedRow) => {
          if (row._isCovered) {
            return (
              <ul className={testListClass}>
                {row.matchingAdoTestCases.map((tc, i) => (
                  <li key={i}>
                    {tc.title}
                    <span className={csBadgeClass}>
                      CS-{(tc.similarity * 100).toFixed(2)}%
                    </span>
                  </li>
                ))}
              </ul>
            );
          }
          const aboveThreshold = row.suggestedTestCases.filter(
            tc => tc.similarity > thresholdValue / 100,
          );
          return (
            <ul className={testListClass}>
              {aboveThreshold.map((tc, i) => (
                <li key={i}>
                  {tc.title}
                  <span className={csBadgeClass}>
                    CS-{(tc.similarity * 100).toFixed(2)}%
                  </span>
                </li>
              ))}
              {aboveThreshold.length === 0 && (
                <li className="italic text-[var(--text-secondary)]">
                  No test case found in ADO for this method
                </li>
              )}
            </ul>
          );
        },
      },
    ],
    [thresholdValue],
  );

  return (
    <div className="w-full">
      <div className="rounded-[14px] border border-[var(--inv-border)] bg-[var(--inv-surface)] p-6 shadow-[var(--inv-shadow)]">
        <h2 className="mb-4 font-serif text-lg font-semibold text-[var(--inv-text)]">
          Test Intelligence — Method Mapping
        </h2>

        {isMappingLoading && (
          <div className={loaderWrapClass}>
            <Spinner className="h-8 w-8" />
            <p className={loaderLabelClass}>
              Ingesting test cases and generating mappings…
            </p>
          </div>
        )}

        {isPolling && (
          <div className={loaderWrapClass}>
            <Spinner className="h-8 w-8" />
            <p className={loaderLabelClass}>
              Checking ingestion and generation status…
            </p>
          </div>
        )}

        {isAnalyzing && (
          <div className={loaderWrapClass}>
            <Spinner className="h-8 w-8" />
            <p className={loaderLabelClass}>Analyzing gap…</p>
          </div>
        )}

        {!isMappingLoading && gapAnalysis !== null && (
          <>
            <p className="mb-6 text-sm leading-normal text-[var(--text-secondary)]">
              <strong>How this works:</strong> Each method with no runtime
              coverage is checked against all ADO Test Cases. A match is found
              when the method name appears in a test case tag or title. Mapped
              means a test case exists — not necessarily executed against this
              build. No Test Case means no test case exists in ADO for this
              method — this is a true gap.
            </p>

            <div className="mb-6 grid grid-cols-2 gap-4 lg:grid-cols-4">
              <StatCard
                title="TOTAL METHODS"
                value={gapAnalysis.summary.totalMethods}
              />
              <StatCard
                title="METHODS MAPPED TO ADO"
                value={gapAnalysis.summary.covered}
              />
              <StatCard
                title="METHODS NOT MAPPED"
                value={gapAnalysis.summary.notCovered}
              />
              <StatCard
                title="MAPPED PERCENTAGE"
                value={`${gapAnalysis.summary.coveragePercentage.toFixed(2)}%`}
              />
            </div>

            <div className="flex items-center justify-end gap-4 pb-4 pt-2">
              <span className="text-[0.8rem] text-[var(--text-secondary)]">
                *CS — Confidence Score {thresholdValue}%
              </span>
              <Button
                type="button"
                onClick={() =>
                  downloadCsv(
                    gapAnalysis.covered,
                    gapAnalysis.notCovered,
                    componentName,
                  )
                }>
                Download
                <Download className="h-5 w-5" />
              </Button>
            </div>

            <OnboardingTable
              data={mergedRows}
              columns={columns}
              rowKey={row => `${row.method}|${row.filePath}|${row._isCovered}`}
            />
          </>
        )}
      </div>
    </div>
  );
};

export default TestMappingView;
